import React, { useMemo } from 'react';
import { ButtonSelect, Flex } from '@upsales/components';
import T from 'Components/Helpers/translate';
import type {
	ActiveFilter,
	ProductTypeFilter,
	RecurringFilter,
	CustomFieldFilters
} from 'Store/reducers/AdminProductListReducer';
import ListViewFilters from 'App/components/ListView/ListViewFilters';
import { ProductCustomField } from 'App/resources/Model/CustomField';
import { getCustomFieldConfig } from 'App/helpers/filterHelper';
import { CustomFieldFilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import { useSoftDeployAccess } from 'App/components/hooks';

type Props = {
	activeFilter: ActiveFilter;
	onActiveFilterChange: (value: ActiveFilter) => void;
	productTypeFilter: ProductTypeFilter;
	onProductTypeFilterChange: (value: ProductTypeFilter) => void;
	recurringFilter: RecurringFilter;
	onRecurringFilterChange: (value: RecurringFilter) => void;
	customFieldFilters?: CustomFieldFilters;
	onCustomFieldFiltersChange?: (filters: CustomFieldFilters) => void;
};

const getFilterConfigs = (customFields: ProductCustomField[]) => {
	return customFields.reduce<{ [filterName: string]: CustomFieldFilterConfig }>((res, customField) => {
		const filterName = `product.Custom_${customField.id}`;
		res[filterName] = getCustomFieldConfig({
			entityType: 'product',
			name: filterName,
			field: customField,
			fieldId: customField.id
		});
		return res;
	}, {});
};

const ProductListFilters = ({
	activeFilter,
	onActiveFilterChange,
	productTypeFilter,
	onProductTypeFilterChange,
	recurringFilter,
	onRecurringFilterChange,
	customFieldFilters = {},
	onCustomFieldFiltersChange
}: Props) => {
	const hasProductBundle = Tools.FeatureHelper.isAvailable(Tools.FeatureHelper.Feature.PRODUCT_BUNDLES);
	const hasRecurringOrder = Tools.FeatureHelper.isAvailable(Tools.FeatureHelper.Feature.RECURRING_ORDER);
	const hasCustomFieldFilter = useSoftDeployAccess('PRODUCT_LIST_CUSTOM_FIELD_FILTER');

	const customFields = useMemo(() => {
		return (Tools.AppService.getCustomFields('product') as ProductCustomField[]).filter(
			cf => cf.$hasAccess && cf.visible
		);
	}, []);

	const filterConfigs = useMemo(() => getFilterConfigs(customFields), [customFields]);

	const hasChanged = Object.keys(customFieldFilters).length > 0;

	return (
		<Flex className="admin-products-filters" gap="16px" alignItems="center">
			{hasProductBundle ? (
				<ButtonSelect
					size="sm"
					value={productTypeFilter}
					options={[
						{ value: 'all' as const, title: T('default.all') },
						{ value: 'products' as const, title: T('default.products') },
						{ value: 'bundles' as const, title: T('admin.products.bundles') }
					]}
					onChange={onProductTypeFilterChange}
				/>
			) : null}
			{hasRecurringOrder ? (
				<ButtonSelect
					size="sm"
					value={recurringFilter}
					options={[
						{ value: 'all' as const, title: T('default.all') },
						{ value: 'oneoff' as const, title: T('admin.products.oneOff') },
						{ value: 'recurring' as const, title: T('admin.products.recurring') }
					]}
					onChange={onRecurringFilterChange}
				/>
			) : null}
			<ButtonSelect
				size="sm"
				value={activeFilter}
				options={[
					{ value: 'all' as const, title: T('default.all') },
					{ value: 'active' as const, title: T('default.active') },
					{ value: 'inactive' as const, title: T('default.inactive') }
				]}
				onChange={onActiveFilterChange}
			/>
			{hasCustomFieldFilter && customFields.length > 0 && onCustomFieldFiltersChange ? (
				<ListViewFilters
					filterConfigs={filterConfigs}
					activeFilters={customFieldFilters}
					onChange={onCustomFieldFiltersChange}
					hasChanged={hasChanged}
					customFields={customFields}
					listType="product"
					opts={{ noTabs: true, alwaysShowButton: true }}
				/>
			) : null}
		</Flex>
	);
};

export default ProductListFilters;
